import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
// router
import { useLoaderData } from 'react-router-dom'
// components
import { motion } from 'framer-motion'
import CarouselOtherNews from './modules/CarouselOtherNews/CarouselOtherNews'
// hooks
import useUtils from '../hooks/useUtils'
// assets
import locationIcon from '../assets/img/map-pin.svg'
// utils
import sanitizedContentData from './shared/utils/sanitizedContentData'
import ComingSoon from './shared/components/ComingSoon'
// styles
import './release.scss'
// helpers
import isEmpty from 'lodash/isEmpty'

export default function ReleaseSingle() {
  const {
    acf: { detail_section = {} },
  } = useLoaderData()
  const { device } = useUtils()

  const [isExpanded, setIsExpanded] = useState(false)
  const [needsTruncation, setNeedsTruncation] = useState(false)
  const [animated, setAnimated] = useState(false)
  const contentRef = useRef(null)

  const maxHeight = useMemo(() => {
    return device === 'mobile' || device === 'tablet' ? 550 : 350
  }, [device])

  const checkTruncation = useCallback(() => {
    if (contentRef.current) {
      const scrollHeight = contentRef.current.scrollHeight
      const shouldTruncate = scrollHeight > maxHeight
      setNeedsTruncation(shouldTruncate)
      if (!shouldTruncate && isExpanded) {
        setIsExpanded(false)
      }
    }
  }, [isExpanded, maxHeight])

  useEffect(() => {
    const timer = setTimeout(() => {
      checkTruncation()
    }, 100)

    const resizeObserver = new ResizeObserver(() => {
      checkTruncation()
    })

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current)
    }

    return () => {
      clearTimeout(timer)
      resizeObserver.disconnect()
    }
  }, [checkTruncation, detail_section?.description])

  useEffect(() => {
    window.addEventListener('resize', checkTruncation)
    return () => {
      window.removeEventListener('resize', checkTruncation)
    }
  }, [checkTruncation])

  const backgroundImage = useMemo(() => {
    if (!detail_section?.backgroundImage) return { url: '', alt: '', title: '' }

    return device === 'desktop' || device === 'desktop-xl' || device === 'tablet'
      ? detail_section?.backgroundImage?.desktopImage
      : detail_section?.backgroundImage?.mobileImage || ''
  }, [device, detail_section])

  const handleToggle = () => {
    if (!animated) setAnimated(true)
    setIsExpanded((prev) => !prev)
  }

  if (!detail_section || isEmpty(detail_section)) {
    return (
      <article className="w-full h-full bg-isabelline flow-root">
        <div className="mt-[186px] mb-16">
          <ComingSoon />
        </div>
      </article>
    )
  }

  return (
    <article className="w-full h-full bg-isabelline flow-root">
      <div className="flex flex-col items-center gap-8 w-max-[1203px] mt-[186px] mb-16 px-10 xl:px-[357px] bg-isabelline">
        {/* Encabezado del press release */}
        <motion.div
          className="relative w-full overflow-hidden flex flex-col gap-8 mt-26 lg:mt-30 w-max-[1203px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <hgroup>
            <p className="self-stretch font-halyard font-normal text-[#1B1F24] tracking-[2px] uppercase">
              {detail_section?.title || ''}
            </p>
            <h1 className="self-stretch font-halyard font-normal text-[45px] lg:text-[70px] text-[#1B1F24] leading-[45px] lg:leading-[70px] uppercase">
              {detail_section?.subTitle || ''}
            </h1>
          </hgroup>
          <div className="flex flex-col-reverse lg:flex-row justify-between items-start self-stretch gap-2">
            <address className="flex items-center gap-2 not-italic">
              <img src={locationIcon} alt="Location Icon" className="object-contain" />
              <span className="text-[#1B1F24] font-halyard text-[20px] font-normal leading-[100%]">
                {detail_section?.location || ''}
              </span>
            </address>
            <time className="text-[#1B1F24] font-halyard text-[20px] font-normal leading-[100%]">
              {detail_section?.date || ''}
            </time>
          </div>
        </motion.div>

        {/* Imagen principal */}
        <figure className="w-full">
          <motion.img
            className="lg:mb-[60px] lg:mt-4 rounded-[20px] bg-[#D9D9D] object-cover sm:h-max-[380px] md:h-max-[380px] w-full h-[100%]"
            src={backgroundImage?.url}
            alt={backgroundImage?.alt}
            title={backgroundImage?.title}
            loading="lazy"
          />
        </figure>

        {/* Cuerpo del comunicado */}
        <section className="w-full">
          <div
            className="relative overflow-hidden"
            style={{
              maxHeight: needsTruncation && !isExpanded ? maxHeight : 9999,
              transition: animated ? 'max-height 0.5s ease-in-out' : 'none',
            }}
          >
            <div
              ref={contentRef}
              className="wysiwyg"
              dangerouslySetInnerHTML={sanitizedContentData(detail_section?.description)}
            />
            <div
              className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none transition-opacity duration-300"
              style={{
                background: 'linear-gradient(to bottom, transparent, #F7F4F0)',
                opacity: !isExpanded && needsTruncation ? 1 : 0,
              }}
            />
          </div>

          {needsTruncation && (
            <div className="w-full mt-6">
              <button
                onClick={handleToggle}
                className="flex items-center justify-center gap-3 py-4 px-6 rounded-[37px] border border-[#1B1F24] text-[#1B1F24] font-halyard text-[20px] font-[500] leading-[100%] tracking-[2px] uppercase transition-colors duration-200 hover:bg-[#1B1F24] hover:text-[#F7F4F0]"
              >
                <span>{isExpanded ? 'Read Less' : 'Read More'}</span>
                <svg
                  className={`transition-transform duration-300 ease-in-out ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 5L8 11L14 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </section>

        {/* Otras noticias relacionadas */}
        <aside>
          <CarouselOtherNews
            data={{
              sectionTitle: 'Other News',
              showRedirectButton: true,
              redirectButton: { url: '/latest-news', title: 'View All' },
            }}
          />
        </aside>
      </div>
    </article>
  )
}
